import React, { useEffect, useMemo, useRef, useState } from 'react';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import moment from 'moment';
import { styles } from './styles';

function parseDate(value) {
  const parsed = moment(value, 'DD/MM/YYYY', true);
  return parsed.isValid() ? parsed : null;
}

function getExpirationDateFromDays(createdAt, days) {
  const createdAtMoment = moment(createdAt);
  if (!createdAtMoment.isValid()) return '';
  if (!days || Number(days) <= 0) return '';

  return createdAtMoment.clone().add(Number(days), 'days').format('DD/MM/YYYY');
}

function toNativeDateValue(value) {
  const parsed = parseDate(value);
  return parsed ? parsed.format('YYYY-MM-DD') : '';
}

function fromNativeDateValue(value) {
  if (!value) return '';
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
}

export default function EditLinkConfigModal({
  open = false,
  onClose,
  onSave,
  checklistTitle = '',
  responsesCount = 0,
  initialDays = 0,
  initialCreatedAt = null,
  initialClosed = false,
  isLinkClosed = false,
}) {
  const nativeDateInputRef = useRef(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [closed, setClosed] = useState(initialClosed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isReadOnly = saving || Boolean(isLinkClosed);
  const closedValue = isLinkClosed ? true : Boolean(closed);

  useEffect(() => {
    if (!open) return;
    const parsedDays = Number.isFinite(initialDays) ? Number(initialDays) : 0;
    setExpirationDate(getExpirationDateFromDays(initialCreatedAt, parsedDays));
    setClosed(isLinkClosed ? true : Boolean(initialClosed));
    setSaving(false);
    setError('');
  }, [open, initialDays, initialCreatedAt, initialClosed, isLinkClosed]);

  useEffect(() => {
    function onEsc(event) {
      if (event.key === 'Escape' && !saving && onClose) onClose();
    }
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose, saving]);

  const parsedExpirationDate = useMemo(
    () => parseDate(expirationDate),
    [expirationDate]
  );
  const createdAtMoment = useMemo(() => {
    const parsed = moment(initialCreatedAt);
    return parsed.isValid() ? parsed : null;
  }, [initialCreatedAt]);

  const days = useMemo(() => {
    if (!parsedExpirationDate || !createdAtMoment) return 0;
    return parsedExpirationDate.diff(
      createdAtMoment.clone().startOf('day'),
      'days'
    );
  }, [parsedExpirationDate, createdAtMoment]);

  const daysLabel = useMemo(() => {
    if (!parsedExpirationDate || !days || days <= 0) return 'Sem expiração';
    return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  }, [days, parsedExpirationDate]);

  const expirationLabel = useMemo(() => {
    if (!parsedExpirationDate || !days || days <= 0) return 'Sem expiração';
    return parsedExpirationDate.format('DD/MM/YYYY');
  }, [days, parsedExpirationDate]);

  if (!open) return null;

  async function handleSave() {
    if (isReadOnly || !onSave) {
      if (onClose) onClose();
      return;
    }

    try {
      if (expirationDate && !parsedExpirationDate) {
        setError('Informe uma data final válida no formato DD/MM/AAAA.');
        return;
      }

      if (expirationDate && !createdAtMoment) {
        setError('Não foi possível identificar a data de criação do link.');
        return;
      }

      if (parsedExpirationDate && createdAtMoment && days <= 0) {
        setError(
          `A data final deve ser posterior a ${createdAtMoment.format(
            'DD/MM/YYYY'
          )}.`
        );
        return;
      }

      setSaving(true);
      setError('');
      await onSave({
        days: Number(days) || 0,
        closed: isLinkClosed ? true : Boolean(closed),
      });
      if (onClose) onClose();
    } catch (event) {
      setError('Nao foi possivel salvar as alteracoes. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  function openCalendar() {
    if (isReadOnly || !nativeDateInputRef.current) return;
    if (typeof nativeDateInputRef.current.showPicker === 'function') {
      nativeDateInputRef.current.showPicker();
      return;
    }
    nativeDateInputRef.current.click();
  }

  return (
    <div
      style={styles.backdrop}
      onMouseDown={event => {
        if (event.target !== event.currentTarget) return;
        if (saving) return;
        if (onClose) onClose();
      }}
      role="presentation"
    >
      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.header}>
          <div style={styles.headerTitle}>Editar Configurações do Link</div>

          <button
            type="button"
            onClick={() => !saving && onClose && onClose()}
            style={styles.closeBtn}
            aria-label="Fechar"
            title="Fechar"
            disabled={saving}
          >
            <HighlightOffOutlinedIcon
              style={{ fontSize: 20, color: '#6B7280' }}
            />
          </button>
        </div>

        <div style={styles.topCard}>
          <div style={styles.topCardTitle}>{checklistTitle}</div>
          <div style={styles.topCardSub}>
            {responsesCount}{' '}
            {responsesCount === 1 ? 'resposta recebida' : 'respostas recebidas'}
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.row}>
          <div style={styles.rowLeft}>
            <div style={styles.rowIcon}>
              <BlockOutlinedIcon style={{ fontSize: 20, color: '#6B7280' }} />
            </div>
            <div>
              <div style={styles.rowTitle}>Fechar Link</div>
              <div style={styles.rowSub}>
                {isLinkClosed
                  ? 'Link ja esta encerrado e nao pode ser reaberto'
                  : 'Impede novas respostas via link'}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (isReadOnly) return;
              setClosed(prev => !prev);
            }}
            style={{
              ...styles.switchWrap,
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
              opacity: isReadOnly ? 0.65 : 1,
              pointerEvents: isReadOnly ? 'none' : 'auto',
            }}
            aria-label="Alternar link fechado"
            disabled={isReadOnly}
          >
            <span style={styles.switchTrack(closedValue)}>
              <span style={styles.switchThumb(closedValue)} />
            </span>
          </button>
        </div>

        <div style={{ ...styles.row, alignItems: 'flex-start' }}>
          <div style={styles.rowLeft}>
            <div style={styles.rowIcon}>
              <AccessTimeOutlinedIcon
                style={{ fontSize: 20, color: '#6B7280' }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={styles.rowTitle}>Expiração do Link</div>
              <div style={styles.rowSub}>
                {expirationLabel === 'Sem expiração'
                  ? 'Defina uma data final para expiração do link'
                  : `O link expira em ${expirationLabel} (${daysLabel})`}
              </div>
            </div>
          </div>

          <div style={styles.expirationControlsRow}>
            <div style={styles.expirationFieldWrap}>
              <input
                ref={nativeDateInputRef}
                type="date"
                value={toNativeDateValue(expirationDate)}
                min={
                  createdAtMoment
                    ? createdAtMoment.clone().add(1, 'day').format('YYYY-MM-DD')
                    : undefined
                }
                onChange={({ target }) => {
                  if (isReadOnly) return;
                  setExpirationDate(fromNativeDateValue(target.value));
                  setError('');
                }}
                style={styles.nativeDateInput}
                tabIndex={-1}
                aria-hidden="true"
                disabled={isReadOnly}
              />

              <div
                style={{
                  ...styles.expirationField,
                  opacity: isReadOnly ? 0.65 : 1,
                }}
              >
                <input
                  type="text"
                  value={expirationDate}
                  onChange={({ target }) => {
                    if (isReadOnly) return;
                    setExpirationDate(target.value);
                    setError('');
                  }}
                  placeholder="DD/MM/AAAA"
                  style={styles.expirationTextInput}
                  disabled={isReadOnly}
                />

                <button
                  type="button"
                  onClick={openCalendar}
                  style={styles.calendarButton}
                  disabled={isReadOnly}
                  aria-label="Selecionar data final"
                >
                  <CalendarTodayOutlinedIcon
                    style={{ fontSize: 18, color: '#6B7280' }}
                  />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (isReadOnly) return;
                setExpirationDate('');
                setError('');
              }}
              style={styles.clearDateBtn}
              disabled={isReadOnly}
            >
              Sem expiração
            </button>
          </div>
        </div>

        <div style={styles.expirationLineWrap}>
          <div style={styles.expirationLine} />
        </div>

        <div style={styles.dateHelperText}>
          {createdAtMoment
            ? `A data final deve ser posterior a ${createdAtMoment.format(
                'DD/MM/YYYY'
              )}.`
            : 'A data final será convertida para dias conforme a regra atual do link.'}
        </div>

        {isLinkClosed ? (
          <div
            style={{
              ...styles.warnBox,
              backgroundColor: '#FEF2F2',
              borderColor: '#FCA5A5',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <HighlightOffOutlinedIcon
              style={{
                fontSize: 18,
                color: '#DC2626',
                flexShrink: 0,
              }}
            />
            <div
              style={{
                ...styles.warnText,
                color: '#DC2626',
                fontWeight: 600,
                lineHeight: '18px',
              }}
            >
              Este link está encerrado e não pode ser editado.
            </div>
          </div>
        ) : (
          <div style={styles.warnBox}>
            <WarningAmberOutlinedIcon
              style={{ fontSize: 18, color: '#F59E0B' }}
            />
            <div style={styles.warnText}>
              Alterações nas configurações não afetam respostas já recebidas.
            </div>
          </div>
        )}

        {!!error && (
          <div
            style={{
              marginTop: 10,
              color: '#B91C1C',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={styles.footer}>
          <button
            type="button"
            style={{ ...styles.cancelBtn, opacity: saving ? 0.6 : 1 }}
            onClick={() => !saving && onClose && onClose()}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...styles.saveBtn,
              opacity: isReadOnly ? 0.55 : 1,
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={isReadOnly}
            title={
              isLinkClosed
                ? 'Link encerrado nao permite edicao'
                : 'Salvar alteracoes'
            }
          >
            <SaveOutlinedIcon style={{ fontSize: 18, color: '#FFFFFF' }} />
            {saving ? 'Salvando...' : 'Salvar Alteracoes'}
          </button>
        </div>
      </div>
    </div>
  );
}
